import { filterConditionsFromFilterObject } from '~/modules/common/services/util/util';
import {
    ApplicationScope,
    Database,
    DatabaseEntry,
    DatabaseTemplate,
    DatabaseTemplateCategory,
    ActivePermission,
    DatabaseField,
    DatabaseUser,
    Organisation,
    DatabaseSettingsDefaultSortDirectionEnum,
    GetEntriesRequest,
    DatabaseContentPage,
    CustomerFinePrints,
    ResponseError
} from "~/api-client";
import { isContactField, sortConditionsFromSortObject } from "~/modules/common/services/util/util";
import { defineStore } from "pinia";
import { LoggingService } from '~/modules/common/plugins/02.service-registry';

const sortResetter = (state: State) => {
    const database = state.currentDatabase;
    return (preset?: { [key: string]: string }) => {
        const setProp = (key: string, val: string | number | null) => state.sortOptions[database.id!][key] = <any>val;
        state.sortOptions[database.id!] = {};
        const sortableFields = database.fields?.filter((field) => field.showInSorter) ?? [];
        sortableFields.forEach((field: DatabaseField) => {
            if (isContactField(field)) {
                const firstNameKey = `${field.id!}.firstName`;
                const lastNameKey = `${field.id!}.lastName`;
                const usernameKey = `${field.id!}.username`;
                setProp(firstNameKey, preset?.[firstNameKey] ?? null);
                setProp(lastNameKey, preset?.[lastNameKey] ?? null);
                setProp(usernameKey, preset?.[usernameKey] ?? null);
            } else {
                setProp(field.id!, preset?.[field.id!] ?? null);
            }
        });
    }
};

const filterResetter = (state: State) => {
    const database = state.currentDatabase;
    return (preset?: { [key: string]: string }) => {
        const setProp = (key: string, val: string | number | null) => state.filterOptions[database.id!][key] = <any>val;
        state.filterOptions[database.id!] = {};
        state.activeFilterId[database.id!] = undefined;
        const filterableFields = database.fields?.filter((field) => field.showInFilter) ?? [];
        filterableFields.forEach((field: DatabaseField) => {
            if (isContactField(field)) {
                const firstNameKey = `${field.id!}.firstName`;
                const lastNameKey = `${field.id!}.lastName`;
                const usernameKey = `${field.id!}.username`;
                setProp(firstNameKey, preset?.[firstNameKey] ?? null);
                setProp(lastNameKey, preset?.[lastNameKey] ?? null);
                setProp(usernameKey, preset?.[usernameKey] ?? null);
            } else {
                setProp(field.id!, preset?.[field.id!] ?? null);
            }
        });
    }
};

type SortOptions = { [key: string]: DatabaseSettingsDefaultSortDirectionEnum };
type FilterOptions = { [key: string]: string };
type GroupByField = string;
type DatabaseTemplatesByCategory = { [key: string]: DatabaseTemplate[] };
type EntriesByDatabase = { [key: string]: DatabaseEntry[] };
type SortOptionsByDatabase = { [key: string]: SortOptions };
type FilterOptionsByDatabase = { [key: string]: FilterOptions };
type GroupByOptionsByDatabase = { [key: string]: GroupByField };
type FilterIdByDatabase = { [key: string]: string | undefined };
type FilteredByDatabase = { [key: string]: boolean };
type GroupedByDatabase = {[key: string]: boolean};
type FieldListByDatabase = { [key: string]: DatabaseField[] };
type ParentEntryByDatabase = { [key: string]: string | undefined };
type SearchTermsByDatabase = { [key: string]: string | undefined };

interface State {
    currentDatabase: Database;
    parentDatabase: Database;
    currentEntries: EntriesByDatabase;
    selectedEntry: DatabaseEntry | null;
    currentContentPages: DatabaseContentPage[];
    currentFinePrints: CustomerFinePrints;
    sortOptions: SortOptionsByDatabase;
    filterOptions: FilterOptionsByDatabase;
    groupByOptions: GroupByOptionsByDatabase;
    filtered: FilteredByDatabase;
    previewFieldList: FieldListByDatabase;
    grouped: GroupedByDatabase;
    activeFilterId: FilterIdByDatabase;
    searchTerms: SearchTermsByDatabase;
    parentEntry: ParentEntryByDatabase;
    databaseTemplates: DatabaseTemplate[];
    databaseTemplateCategories: DatabaseTemplateCategory[];
    databaseTemplatesByCategory: DatabaseTemplatesByCategory;
    databasePermissions: ActivePermission;
    databaseUsers: DatabaseUser[];
    organisations: Organisation[];
    flyerState: {
        activePageIdx: number;
        isReadOnly: boolean;
    },
    finePrintsTrigger: HTMLElement | null;
}

const handleErrorResponse = ($logger: LoggingService, prefix: string) =>  async (error: any) => {
    if (error instanceof ResponseError) {
        const msg = await error.response.text();
        $logger.error(prefix, msg);
        throw new Error(msg);
    } else {
        $logger.error(prefix, error);
        throw error;
    }
}

export const useMyDbStore = defineStore('mydb', {
    state: (): State => ({
        currentDatabase: {},
        parentDatabase: {},
        currentEntries: {},
        selectedEntry: null,
        currentContentPages: [],
        currentFinePrints: {},
        sortOptions: {},
        filterOptions: {},
        groupByOptions: {},
        grouped: {},
        filtered: {},
        previewFieldList: {},
        activeFilterId: {},
        searchTerms: {},
        parentEntry: {},
        databaseTemplates: [],
        databaseTemplateCategories: [],
        databaseTemplatesByCategory: {},
        databasePermissions: {},
        databaseUsers: [],
        organisations: [],
        flyerState: {
            activePageIdx: 0,
            isReadOnly: false
        },
        finePrintsTrigger: null
    }),
    getters: {
        allDatabaseTemplates: (state) => (state.databaseTemplates),
    },
    actions: {
        loadDatabaseEntriesFor(databaseId?: string, parentId?: string): Promise<any> {
            const id = databaseId ?? this.$state.currentDatabase.id!;
            const orderBy = sortConditionsFromSortObject(this.$state.sortOptions[id]);
            const filter = filterConditionsFromFilterObject(this.$state.filterOptions[id]);
            const searchTerm = this.$state.searchTerms[id];
            const currentParent = parentId ?? this.$state.parentEntry[id];
            const params: GetEntriesRequest = {
                xApplicationScope: ApplicationScope.MyDb,
                databaseId: id,
                ...(!!currentParent && { belongsTo: currentParent }),
                ...(!!orderBy && orderBy.length > 0 && { orderBy }),
                ...(!!filter && filter.length > 0 && { filter }),
                ...(!!searchTerm && { searchTerm })
            };

            const { $backendService, $logger } = useNuxtApp();

            return $backendService.databaseControllerApi.getEntries(params).then((result) => {
                if (!result) {
                    throw new Error("unable to load database entries");
                }
                this.SET_CURRENT_ENTRIES(id, result);
                $logger.info("loaded database entries");
            }).catch((error) => {
                this.SET_CURRENT_ENTRIES(id, []);
                handleErrorResponse($logger, "error fetching database entries")(error);
            });
        },
        addEntry(database: Database, entry: DatabaseEntry): Promise<DatabaseEntry> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.createEntry({
                xApplicationScope: ApplicationScope.MyDb,
                databaseId: database.id!,
                databaseEntryUpdateCreate: entry
            }).then((result) => {
                if (!result) {
                    throw new Error("error creating entry");
                }
                this.ADD_ENTRY(database.id!, result);
                $logger.info("created database entry");
                return result;
            }).catch(handleErrorResponse($logger, "error creating database entry"));
        },
        updateEntry(database: Database, entry: DatabaseEntry): Promise<DatabaseEntry> {
            // filter out fieldValues which are not defined anymore
            const definedFields = database.fields?.map((field) => field.id) ?? [];
            const fieldsValues = entry.fieldsValues;
            if (fieldsValues) {
                entry.fieldsValues = Object.fromEntries(Object.entries(fieldsValues).filter(([key]) => definedFields.includes(key)));
            }
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.updateEntry({ xApplicationScope: ApplicationScope.MyDb, databaseId: database.id!, entryId: entry.id!, databaseEntryUpdateCreate: entry }).then((result) => {
                if (!result) {
                    throw new Error("error updating entry");
                }
                this.UPDATE_ENTRY(database.id!, result);
                $logger.info("updated database entry");
                return result;
            }).catch(handleErrorResponse($logger, "error updating database entry"));
        },
        deleteEntry(databaseId: string, entryId: string): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.deleteEntry({ xApplicationScope: ApplicationScope.MyDb, databaseId, entryId }).then((result) => {
                this.REMOVE_ENTRY(databaseId, entryId);
                $logger.info("removed database entry");
                return true;
            }).catch(handleErrorResponse($logger, "error deleting database entry"));
        },
        deleteEntries(databaseId: string, entriesIds: string[]): Promise<Array<string>> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.deleteEntries({
                xApplicationScope: ApplicationScope.MyDb,
                databaseId: databaseId,
                requestBody: entriesIds
            }).then((deletedEntriesIds: string[]) => {
                deletedEntriesIds.forEach(entryId => {
                    this.REMOVE_ENTRY(databaseId, entryId);
                });
                $logger.info("removed database entries");
                return deletedEntriesIds;
            }).catch(handleErrorResponse($logger, "error deleting database entries"));
        },
        loadDatabaseTemplates(): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return Promise.all([
                $backendService.databaseTemplateControllerApi.getDatabaseTemplates({ xApplicationScope: ApplicationScope.MyDb }),
                $backendService.databaseTemplateControllerApi.getDatabaseTemplateCategories({ xApplicationScope: ApplicationScope.MyDb })
            ]).then((result) => {
                if (!result) {
                    throw new Error("unable to load database templates");
                }

                const [templates, categories] = result;
                categories.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
                templates.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);

                const templatesByCategory = templates
                    .filter((template) => !!template.categoryId)
                    .reduce((prev, current) => {
                        const key = current.categoryId!;
                        if (!prev[key]) {
                            prev[key] = [];
                        }
                        prev[key].push(current);
                        return prev;
                    }, {} as DatabaseTemplatesByCategory);

                this.SET_DATABASE_TEMPLATES(templates);
                this.SET_DATABASE_TEMPLATES_BY_CATEGORY(templatesByCategory);
                this.SET_DATABASE_TEMPLATE_CATEGORIES(categories);
                $logger.info("loaded database templates");
            }).catch(handleErrorResponse($logger, "error fetching database templates"));
        },
        deleteDatabaseTemplateById(template: DatabaseTemplate): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseTemplateControllerApi.deleteDatabaseTemplate({ xApplicationScope: ApplicationScope.MyDb, templateId: template.id! }).then(() => {
                this.REMOVE_DATABASE_TEMPLATE(template);
                $logger.info("database template deleted");
            }).catch(handleErrorResponse($logger, "error deleting database template"));
        },
        loadDatabaseTemplateCategories(): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseTemplateControllerApi.getDatabaseTemplateCategories({ xApplicationScope: ApplicationScope.MyDb }).then((result) => {
                if (!result) {
                    throw new Error("unable to load database template categories");
                }
                this.SET_DATABASE_TEMPLATE_CATEGORIES(result);
                $logger.info("loaded database template categories");
            }).catch(handleErrorResponse($logger, "error fetching database template categories"));
        },
        loadOrganisations(): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.organisationControllerApi.getAllOrganisations({ xApplicationScope: ApplicationScope.MyDb }).then((result) => {
                if (!result) {
                    throw new Error("unable to load organisations");
                }
                this.SET_ORGANISATIONS(result);
                $logger.info("loaded organisations");
            }).catch(handleErrorResponse($logger, "error fetching organisations"));
        },
        loadDatabasePermissions(databaseId: string): Promise<any> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.getActivePermissions({ xApplicationScope: ApplicationScope.MyDb, databaseId }).then((result) => {
                if (!result) {
                    throw new Error("unable to load permissions");
                }

                if (Object.values(result).every(val => val === false)) {
                    throw new Error("No active permissions!");
                }
                this.SET_DATABASE_PERMISSIONS(result);
                $logger.info("loaded database permissions");
                return result;
            }).catch(handleErrorResponse($logger, "error fetching database permissions"));
        },
        loadDatabaseUsers(databaseId: string): Promise<DatabaseUser[]> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.getDatabaseUsers({ xApplicationScope: ApplicationScope.MyDb, databaseId }).then((result) => {
                if (!result) {
                    throw new Error("unable to load database users");
                }

                this.SET_DATABASE_USERS(result);
                $logger.info("loaded database users");
                return result
            }).catch(handleErrorResponse($logger, "error fetching database users"));
        },
        loadParentDatabase(databaseId: string): Promise<DatabaseUser[]> {
            const { $backendService, $logger } = useNuxtApp();
            return $backendService.databaseControllerApi.getDatabaseUsers({ xApplicationScope: ApplicationScope.MyDb, databaseId }).then((result) => {
                if (!result) {
                    throw new Error("unable to load database users");
                }

                this.SET_DATABASE_USERS(result);
                $logger.info("loaded database users");
                return result
            }).catch(handleErrorResponse($logger, "error fetching database users"));
        },
        setSelectedEntry(entry: DatabaseEntry | null) {
            this.$state.selectedEntry = entry;
        },
        SET_CURRENT_ENTRIES(databaseId: string, data: DatabaseEntry[]) {
            this.$state.currentEntries[databaseId] = data;
        },
        ADD_ENTRY(databaseId: string, data: DatabaseEntry) {
            this.$state.currentEntries[databaseId].push(data);
        },
        UPDATE_ENTRY(databaseId: string, data: DatabaseEntry) {
            // note that indexOf won't work because data is disconnected from the store!
            const currentEntries = this.$state.currentEntries[databaseId];
            const idx = currentEntries.findIndex(e => e.id === data.id);
            currentEntries[idx] = data;
            this.$state.currentEntries[databaseId] = [...this.$state.currentEntries[databaseId]];
        },
        REMOVE_ENTRY(databaseId: string, data: string) {
            const currentEntries = this.$state.currentEntries[databaseId] as DatabaseEntry[];
            const idx = currentEntries.findIndex(e => e.id === data);
            this.$state.currentEntries[databaseId] = [
                ...currentEntries.slice(0, idx),
                ...currentEntries.slice(idx + 1)
            ];
        },
        SET_DATABASE_TEMPLATES(data: DatabaseTemplate[]) {
            this.$state.databaseTemplates = data
        },
        SET_DATABASE_TEMPLATES_BY_CATEGORY(data: DatabaseTemplatesByCategory) {
            this.$state.databaseTemplatesByCategory = data
        },
        REMOVE_DATABASE_TEMPLATE(data: DatabaseTemplate) {
            const idx = this.$state.databaseTemplates.indexOf(data);
            this.$state.databaseTemplates.splice(idx, 1);
        },
        SET_DATABASE_TEMPLATE_CATEGORIES(data: DatabaseTemplateCategory[]) {
            this.$state.databaseTemplateCategories = data;
        },
        SET_DATABASE_PERMISSIONS(data: ActivePermission) {
            this.$state.databasePermissions = data;
        },
        SET_DATABASE_ENTRY_POSITION(databaseId: string, oldIndex: number, newIndex: number, currentEntry: DatabaseEntry, replacedEntry: DatabaseEntry, group?: string) {
            const currentEntries = this.$state.currentEntries[databaseId] as DatabaseEntry[];
            let globalOldIndex = oldIndex;
            let globalNewIndex = newIndex;
            if (group) {
                globalOldIndex = currentEntries.findIndex((e) => e.id === currentEntry.id);
                globalNewIndex = currentEntries.findIndex((e) => e.id === replacedEntry.id);
            }
            const entry = currentEntries[globalOldIndex];
            currentEntries.splice(globalOldIndex, 1);
            currentEntries.splice(globalNewIndex, 0, entry);
        },
        UPDATE_DATABASE_ENTRY_RANK(databaseId: string, entryId: string, rank: string) {
            const currentEntries = this.$state.currentEntries[databaseId] as DatabaseEntry[];
            const entry = currentEntries.find((entry) => entry.id === entryId);
            if (entry) {
                // @ts-ignore
                entry.rank = rank;
            }
        },
        SET_DATABASE_USERS(users: DatabaseUser[]) {
            this.$state.databaseUsers = users;
        },
        SET_ORGANISATIONS(organisations: Organisation[]) {
            this.$state.organisations = organisations;
        },
        SET_SORT_OPTIONS(orderBy: SortOptions) {
            this.$state.sortOptions[this.$state.currentDatabase.id!] = orderBy;
        },
        RESET_SORT_OPTIONS() {
            const resetSort = sortResetter(this.$state);
            resetSort();
        },
        SET_FILTER_OPTIONS(filterBy: FilterOptions, filterId?: string) {
            this.$state.filterOptions[this.$state.currentDatabase.id!] = filterBy;
            this.$state.filtered = {
                ...this.$state.filtered,
                [this.$state.currentDatabase.id!]: true
            };
            this.$state.activeFilterId = {
                ...this.$state.activeFilterId,
                [this.$state.currentDatabase.id!]: filterId
            };
        },
        RESET_FILTER_OPTIONS() {
            const resetFilter = filterResetter(this.$state);
            resetFilter();
            this.$state.filtered = {
                ...this.$state.filtered,
                [this.$state.currentDatabase.id!]: false
            };
        },
        setGroupByField(groupByField: string) {
            this.$state.groupByOptions[this.$state.currentDatabase.id!] = groupByField;
            this.$state.grouped = {
                ...this.$state.grouped,
                [this.$state.currentDatabase.id!]: true
            };
        },
        resetGroupByField() {
            this.$state.groupByOptions[this.$state.currentDatabase.id!] = this.$state.currentDatabase?.settings?.groupBy ?? '';
            this.$state.grouped = {
                ...this.$state.grouped,
                [this.$state.currentDatabase.id!]: !!this.$state.groupByOptions[this.$state.currentDatabase.id!]
            };
        },
        setPreviewFieldList(fieldIds: string[]) {
            this.$state.previewFieldList[this.$state.currentDatabase.id!] = this.$state.currentDatabase.fields?.filter((field) => field.displayPreviewOptions?.list && fieldIds.includes(field.id!)) ?? [];
        },
        resetPreviewFieldList() {
            this.$state.previewFieldList[this.$state.currentDatabase.id!] = this.$state.currentDatabase.fields?.filter((field) => field.displayPreviewOptions?.list) ?? [];
        },
        SET_SEARCH_TERM(term: string) {
            this.$state.searchTerms[this.$state.currentDatabase.id!] = term.trim();
            this.$state.filtered = {
                ...this.$state.filtered,
                [this.$state.currentDatabase.id!]: true
            };
        },
        RESET_SEARCH_TERM() {
            this.$state.searchTerms[this.$state.currentDatabase.id!] = undefined;
            this.$state.filtered = {
                ...this.$state.filtered,
                [this.$state.currentDatabase.id!]: false
            };
        },
        SET_PARENT_ENTRY(parentId: string) {
            this.$state.parentEntry[this.$state.currentDatabase.id!] = parentId;
        },
        SET_CURRENT_DATABASE(database: Database) {
            // JSON parse/stringify to decouple from parameter
            this.$state.currentDatabase = JSON.parse(JSON.stringify(database));
            const initSort = sortResetter(this.$state);
            const initFilter = filterResetter(this.$state);
            // initialize parent
            this.$state.parentEntry[database.id!] = undefined;
            // initialize filters
            this.$state.filterOptions[database.id!] = {};
            initFilter();
            // initialize order by
            const defaultSortBy = database.settings?.defaultSortBy ?? null;
            const defaultSortDirection = database.settings?.defaultSortDirection ?? 'ASC';
            const orderBy = defaultSortBy ? { [defaultSortBy]: defaultSortDirection } : undefined;
            initSort(orderBy);
            // initialize group by
            this.$state.groupByOptions[database.id!] = database.settings?.groupBy ?? '';
            this.$state.grouped[database.id!] = !!database.settings?.groupBy;
            // initialize preview field list
            this.$state.previewFieldList[database.id!] = database.fields?.filter((field) => field.displayPreviewOptions?.list) ?? [];
            // initialize search term
            this.$state.searchTerms[database.id!] = undefined;
        },
        SET_CURRENT_PARENT_DATABASE(parentDatabase: Database) {
            this.$state.parentDatabase = JSON.parse(JSON.stringify(parentDatabase));
        },
        SET_CURRENT_CONTENT_PAGES(contentPages: DatabaseContentPage[]) {
            this.$state.currentContentPages = contentPages;
        },
        SET_CURRENT_FINE_PRINTS(finePrints: CustomerFinePrints) {
            this.$state.currentFinePrints = finePrints;
        },
        setActivePageIdx(idx: number) {
            this.$state.flyerState.activePageIdx = idx;
        },
        setFlyerReadOnly(isReadOnly: boolean) {
            this.$state.flyerState.isReadOnly = isReadOnly;
        },
        setFinePrintsTrigger(trigger: HTMLElement | null) {
            this.$state.finePrintsTrigger = trigger;
        }
    }
})
